import React from 'react'
import { motion } from 'framer-motion'
import { Code, Database, Settings, Cloud, Layers, Activity } from 'lucide-react'
import { Skills as SkillsType } from '../types'
import { fadeUp, staggerContainer, viewportOnce, cardHover } from '../lib/motion'

interface SkillsProps {
  skills: SkillsType
}

const Skills: React.FC<SkillsProps> = ({ skills }) => {
  const skillCategories = [
    {
      title: 'Programming Languages',
      icon: <Code className="h-6 w-6" />,
      skills: skills.programming,
      color: 'primary'
    },
    {
      title: 'Frameworks & Libraries',
      icon: <Layers className="h-6 w-6" />,
      skills: skills.frameworks,
      color: 'blue'
    },
    {
      title: 'Databases',
      icon: <Database className="h-6 w-6" />,
      skills: skills.databases,
      color: 'green'
    },
    {
      title: 'Tools & Platforms',
      icon: <Settings className="h-6 w-6" />,
      skills: skills.tools,
      color: 'purple'
    },
    {
      title: 'Technologies',
      icon: <Cloud className="h-6 w-6" />,
      skills: skills.technologies,
      color: 'orange'
    },
    {
      title: 'IT Operations & Observability',
      icon: <Activity className="h-6 w-6" />,
      skills: skills.operations,
      color: 'primary'
    }
  ]

  const getColorClasses = (color: string) => {
    const colorMap = {
      primary: {
        bg: 'bg-primary-100 dark:bg-primary-900/30',
        text: 'text-primary-600 dark:text-primary-400',
        border: 'border-primary-200 dark:border-primary-800',
        skill: 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
      },
      blue: {
        bg: 'bg-accent-100 dark:bg-accent-900/30',
        text: 'text-accent-600 dark:text-accent-400',
        border: 'border-accent-200 dark:border-accent-800',
        skill: 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-600 dark:text-green-400',
        border: 'border-green-200 dark:border-green-800',
        skill: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
      },
      purple: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-600 dark:text-purple-400',
        border: 'border-purple-200 dark:border-purple-800',
        skill: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
      },
      orange: {
        bg: 'bg-orange-100 dark:bg-orange-900/30',
        text: 'text-orange-600 dark:text-orange-400',
        border: 'border-orange-200 dark:border-orange-800',
        skill: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
      }
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.primary
  }

  const allSkills = Object.values(skills).flat()
  // const totalSkills = allSkills.length
  const uniqueSkills = new Set(allSkills).size

  return (
    <section id="skills" className="py-20 bg-white dark:bg-gray-900">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Skills & Technologies
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            A comprehensive overview of my technical expertise and the tools I use to build robust solutions
          </p>
        </motion.div>

        {/* Skills Grid */}
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {skillCategories.map((category, index) => {
            const colors = getColorClasses(category.color)
            return (
              <motion.div
                key={index}
                className={`card p-4 sm:p-6 border-l-4 ${colors.border}`}
                variants={fadeUp}
                whileHover={cardHover.whileHover}
                transition={cardHover.transition}
              >
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className={`p-2 sm:p-3 ${colors.bg} rounded-lg mr-3 sm:mr-4`}>
                    <div className={colors.text}>
                      {React.cloneElement(category.icon, { className: "h-5 w-5 sm:h-6 sm:w-6" })}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                      {category.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {category.skills.length} skills
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {category.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${colors.skill} hover:scale-105 transition-transform duration-200 cursor-default`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Skills Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-12 sm:mb-16">
          <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {skillCategories.length}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Categories</div>
          </div>
          <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {uniqueSkills}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Unique Skills</div>
          </div>
          <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {skills.programming.length}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Languages</div>
          </div>
          <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {skills.tools.length}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Tools</div>
          </div>
        </div>

        {/* Skill Proficiency
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Core Competencies
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Backend Development</h4>
              <div className="space-y-3">
                {['Go', 'PHP', 'JavaScript', 'Microservices'].map((skill, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">{skill}</span>
                    <div className="flex-1 mx-4 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${90 - index * 5}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{90 - index * 5}%</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">DevOps & Cloud</h4>
              <div className="space-y-3">
                {['Docker', 'AWS', 'CI/CD', 'Kubernetes'].map((skill, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">{skill}</span>
                    <div className="flex-1 mx-4 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-green-500 to-accent-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${85 - index * 5}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{85 - index * 5}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  )
}

export default Skills
